import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { RiAddLine, RiArrowLeftLine, RiRefreshLine } from '@remixicon/react';

import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { Card } from '@/design-system/card';
import {
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
} from '@/design-system/empty-state';
import { Field } from '@/design-system/field';
import { FormModal } from '@/design-system/form-modal';
import { Hint } from '@/design-system/hint';
import { Input, InputRoot } from '@/design-system/input';
import { Label } from '@/design-system/label';
import { Paginator } from '@/design-system/pagination';
import { SearchField } from '@/design-system/search-field';
import {
  SegmentedControl,
  SegmentedItem,
} from '@/design-system/segmented-control';
import { Switch } from '@/design-system/switch';
import { TableData, type TableDataColumn } from '@/design-system/table-data';
import { Tabs, TabsList, TabsTrigger } from '@/design-system/tabs';
import { toast } from '@/design-system/toaster';
import { isUjpApprover } from '@/config/ujp-permissions';
import { useAuth } from '@/contexts/AuthContext';
import {
  useUrlBool,
  useUrlNumber,
  useUrlParam,
  useUrlPatch,
  useUrlSearch,
} from '@/hooks/urlState';
import {
  UJP_ENERGY_TYPES,
  UJP_FUEL_TYPES,
  UJP_FUEL_TYPES_BY_ENERGY,
  ujpAPI,
  type CreateUjpEnergyPricePayload,
  type UjpEMoney,
  type UjpEnergyPrice,
  type UjpEnergyType,
  type UjpFuelType,
  type UjpSubconVendor,
  type UjpVehicle,
  type UpsertUjpSubconVendorPayload,
  type UpsertUjpVehiclePayload,
} from '@/services/api/ujp';

import MoneyInput from './components/MoneyInput';
import UjpMasterFilterPanel, {
  countActiveFilters,
  EMPTY_UJP_MASTER_FILTERS,
  type UjpMasterFilterValue,
} from './components/UjpMasterFilterPanel';
import { UJP_COPY } from './copy';
import { formatRupiah, todayIso, toNum } from './format';

/**
 * `/ujp/masters` (TODO-22) — the three UJP masters the wizard reads:
 * vehicles (plate → energy / fuel / baseline), dated energy prices (one row
 * per fuel per effective day, never edited — a change is a NEW dated row) and
 * subcon vendors (bank block for a subcon payee).
 *
 * Approver-gated like `/ujp/config` and `/ujp/tariffs`: the server 403s
 * writes from anyone else, this page is the affordance. Editing a master
 * never rewrites a submitted UJP — every UJP row stores the values it used.
 */

const c = UJP_COPY.masters;

type MastersTab = 'vehicles' | 'energy' | 'vendors';
const TABS: MastersTab[] = ['vehicles', 'energy', 'vendors'];
/** Params owned by one tab's list; a tab switch drops them so they don't bleed over. */
const TAB_LIST_PARAMS = ['page', 'pageSize', 'q', 'inactive', 'fuel', 'unit'] as const;
/** URL keys of the applied filter object (the draft stays in local state). */
const FILTER_PARAMS = { fuel: 'fuel', unit: 'unit', inactive: 'inactive' } as const;
const PAGE_SIZE_DEFAULT = 20;

const eyebrow =
  'text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600';

// ─────────────────────────────────────────────────────────────────────────────
// Drafts + validation (mirror the DTO rules client-side)
// ─────────────────────────────────────────────────────────────────────────────

export interface VehicleDraft {
  id: string | null;
  plateNumber: string;
  unitType: string;
  energyType: UjpEnergyType;
  fuelType: UjpFuelType | '';
  /** Decimal strings as typed; sent as numbers. */
  baseline: string;
  konsumsiPerKm: string;
  /** Default e-money on this truck; `''` = none set. */
  defaultEMoney: UjpEMoney | '';
  /** Physical e-money card number on this truck; `''` = none set. */
  eMoneyCardNo: string;
  active: boolean;
}

export const blankVehicle = (): VehicleDraft => ({
  id: null,
  plateNumber: '',
  unitType: '',
  energyType: 'ICE',
  fuelType: 'SOLAR',
  baseline: '',
  konsumsiPerKm: '',
  defaultEMoney: '',
  eMoneyCardNo: '',
  active: true,
});

export const vehicleToDraft = (v: UjpVehicle): VehicleDraft => ({
  id: v.id,
  plateNumber: v.plateNumber,
  unitType: v.unitType ?? '',
  energyType: v.energyType,
  fuelType: v.fuelType ?? '',
  baseline: v.baseline != null ? String(v.baseline) : '',
  konsumsiPerKm: v.konsumsiPerKm != null ? String(v.konsumsiPerKm) : '',
  defaultEMoney: v.defaultEMoney ?? '',
  eMoneyCardNo: v.eMoneyCardNo ?? '',
  active: v.active,
});

/** The ICE / EV rule: ICE needs baseline > 0, EV needs konsumsiPerKm > 0. */
export const validateVehicleDraft = (d: VehicleDraft): string | null => {
  const v = c.vehicles.validation;
  if (!d.plateNumber.trim()) return v.plate;
  if (!d.fuelType || !UJP_FUEL_TYPES_BY_ENERGY[d.energyType].includes(d.fuelType)) {
    return v.fuel;
  }
  if (d.energyType === 'ICE' && !(toNum(d.baseline) > 0)) return v.baseline;
  if (d.energyType === 'EV' && !(toNum(d.konsumsiPerKm) > 0)) return v.konsumsi;
  return null;
};

export const toVehiclePayload = (d: VehicleDraft): UpsertUjpVehiclePayload => ({
  plateNumber: d.plateNumber.trim().toUpperCase(),
  unitType: d.unitType.trim() || null,
  energyType: d.energyType,
  fuelType: d.fuelType as UjpFuelType,
  baseline: d.energyType === 'ICE' ? toNum(d.baseline) : null,
  konsumsiPerKm: d.energyType === 'EV' ? toNum(d.konsumsiPerKm) : null,
  defaultEMoney: d.defaultEMoney || null,
  eMoneyCardNo: d.eMoneyCardNo.trim() || null,
  active: d.active,
});

export interface VendorDraft {
  id: string | null;
  name: string;
  city: string;
  bankName: string;
  accountNumber: string;
  accountHolder: string;
  picName: string;
  picPhone: string;
  active: boolean;
}

export const blankVendor = (): VendorDraft => ({
  id: null,
  name: '',
  city: '',
  bankName: '',
  accountNumber: '',
  accountHolder: '',
  picName: '',
  picPhone: '',
  active: true,
});

export const vendorToDraft = (v: UjpSubconVendor): VendorDraft => ({
  id: v.id,
  name: v.name,
  city: v.city ?? '',
  bankName: v.bankName ?? '',
  accountNumber: v.accountNumber ?? '',
  accountHolder: v.accountHolder ?? '',
  picName: v.picName ?? '',
  picPhone: v.picPhone ?? '',
  active: v.active,
});

export const validateVendorDraft = (d: VendorDraft): string | null =>
  d.name.trim() ? null : c.vendors.validation.name;

const orNull = (s: string): string | null => s.trim() || null;

export const toVendorPayload = (d: VendorDraft): UpsertUjpSubconVendorPayload => ({
  name: d.name.trim(),
  city: orNull(d.city),
  bankName: orNull(d.bankName),
  accountNumber: orNull(d.accountNumber),
  accountHolder: orNull(d.accountHolder),
  picName: orNull(d.picName),
  picPhone: orNull(d.picPhone),
  active: d.active,
});

export interface PriceDraft {
  fuelType: UjpFuelType;
  /** Region for this price; empty = global default (R3). */
  region: string;
  /** Digits string (MoneyInput). */
  price: string;
  effectiveFrom: string;
}

export const blankPrice = (fuelType: UjpFuelType = 'SOLAR'): PriceDraft => ({
  fuelType,
  region: '',
  price: '',
  effectiveFrom: todayIso(),
});

export const validatePriceDraft = (d: PriceDraft): string | null => {
  if (!(toNum(d.price) > 0)) return c.energy.validation.price;
  if (!d.effectiveFrom) return c.energy.validation.effectiveFrom;
  return null;
};

export const toPricePayload = (d: PriceDraft): CreateUjpEnergyPricePayload => ({
  fuelType: d.fuelType,
  // Empty region = the global default price.
  region: d.region.trim() || null,
  price: d.price,
  effectiveFrom: d.effectiveFrom,
});

/** `1300099887` → `****9887` for anyone outside the approver list. */
const maskAccount = (acc: string | null): string => {
  if (!acc) return '—';
  return acc.length > 4 ? `****${acc.slice(-4)}` : '****';
};

const unitFor = (fuel: UjpFuelType): string =>
  fuel === 'EV_KWH' ? c.energy.perKwh : c.energy.perLiter;

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────

const UjpMastersPage: React.FC = () => {
  const { user } = useAuth();

  // Tab, filters and pagination are URL-driven, the same shape as /packages:
  // the view is shareable, Back works, and a filter change drops `page` in the
  // same URL write (resetPage) so it is one fetch, not two.
  const [tab, setTabParam] = useUrlParam<MastersTab>('tab', 'vehicles', {
    allowed: TABS,
    resetParams: TAB_LIST_PARAMS,
    history: 'push',
  });

  // Vehicles + fuel prices are ops-editable (any signed-in back-office user);
  // vendors stay approver-only (bank details). Tab-aware since the page shows
  // one tab at a time. Mirrors the BE (masters open, vendor approver-gated).
  const canEdit =
    tab === 'vendors' ? isUjpApprover(user?.email) : !!user?.email;

  const [page, setPage] = useUrlNumber('page', 1, { min: 1 });
  const [pageSize, setPageSize] = useUrlNumber('pageSize', PAGE_SIZE_DEFAULT, {
    min: 1,
    resetPage: true,
  });
  // Applied filters are derived from the URL; the draft stays local so the
  // popover can be cancelled. Apply/reset write every field in one patch.
  const [showInactive] = useUrlBool(FILTER_PARAMS.inactive, false);
  // One fuel filter for both the vehicles tab (truck fuel) and the energy tab
  // (price history); a tab switch clears it.
  const [fuelFilter] = useUrlParam<UjpFuelType | ''>(FILTER_PARAMS.fuel, '', {
    allowed: UJP_FUEL_TYPES,
  });
  const [unitFilter] = useUrlParam<string>(FILTER_PARAMS.unit, '');
  const { patch: patchUrl } = useUrlPatch();
  const filters = useMemo<UjpMasterFilterValue>(
    () => ({ fuelType: fuelFilter, unitType: unitFilter, showInactive }),
    [fuelFilter, unitFilter, showInactive]
  );
  const [filterDraft, setFilterDraft] = useState<UjpMasterFilterValue>(
    EMPTY_UJP_MASTER_FILTERS
  );
  const [filterOpen, setFilterOpen] = useState(false);
  const activeFilterCount = useMemo(
    () => countActiveFilters(tab, filters),
    [tab, filters]
  );

  // Seed the draft from the applied filters whenever the popover opens.
  const handleFilterOpenChange = (open: boolean) => {
    if (open) setFilterDraft(filters);
    setFilterOpen(open);
  };

  // Write every filter field + drop `page` in ONE URL patch (one fetch, no
  // stale-page race). Only the active tab's fields are written.
  const writeFilters = (v: UjpMasterFilterValue) => {
    patchUrl((params) => {
      const setOrDelete = (key: string, value: string) => {
        if (value) params.set(key, value);
        else params.delete(key);
      };
      setOrDelete(FILTER_PARAMS.fuel, tab === 'vendors' ? '' : v.fuelType);
      setOrDelete(FILTER_PARAMS.unit, tab === 'vehicles' ? v.unitType : '');
      setOrDelete(
        FILTER_PARAMS.inactive,
        tab !== 'energy' && v.showInactive ? '1' : ''
      );
      params.delete('page');
    });
  };

  const applyFilter = () => {
    writeFilters(filterDraft);
    setFilterOpen(false);
  };

  const resetFilter = () => {
    setFilterDraft(EMPTY_UJP_MASTER_FILTERS);
    writeFilters(EMPTY_UJP_MASTER_FILTERS);
    setFilterOpen(false);
  };

  // Unit-type options for the vehicles filter: what ops actually typed on the
  // trucks. Refreshed after a vehicle is saved (a new unit type may appear).
  const [unitTypes, setUnitTypes] = useState<string[]>([]);
  const [unitTypesLoading, setUnitTypesLoading] = useState(false);
  const [unitTypesVersion, setUnitTypesVersion] = useState(0);
  useEffect(() => {
    if (tab !== 'vehicles') return;
    let cancelled = false;
    setUnitTypesLoading(true);
    ujpAPI
      .listVehicleUnitTypes()
      .then((rows) => !cancelled && setUnitTypes(rows))
      .catch(() => !cancelled && setUnitTypes([]))
      .finally(() => !cancelled && setUnitTypesLoading(false));
    return () => {
      cancelled = true;
    };
  }, [tab, unitTypesVersion]);
  const {
    value: search,
    setValue: setSearch,
    committed: debouncedSearch,
  } = useUrlSearch('q', { resetPage: true, delay: 500 });

  const [vehicles, setVehicles] = useState<UjpVehicle[]>([]);
  const [vendors, setVendors] = useState<UjpSubconVendor[]>([]);
  const [history, setHistory] = useState<UjpEnergyPrice[]>([]);
  const [current, setCurrent] = useState<UjpEnergyPrice[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTab = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (tab === 'vehicles') {
        const res = await ujpAPI.listVehiclesPage({
          search: debouncedSearch,
          includeInactive: showInactive,
          fuelType: fuelFilter || undefined,
          unitType: unitFilter || undefined,
          page,
          limit: pageSize,
        });
        setVehicles(res.data);
        setTotal(res.total);
      } else if (tab === 'vendors') {
        const res = await ujpAPI.listSubconVendorsPage({
          search: debouncedSearch,
          includeInactive: showInactive,
          page,
          limit: pageSize,
        });
        setVendors(res.data);
        setTotal(res.total);
      } else {
        const [res, today] = await Promise.all([
          ujpAPI.listEnergyPriceHistoryPage({
            fuelType: fuelFilter || undefined,
            page,
            limit: pageSize,
          }),
          ujpAPI.listEnergyPrices(todayIso()),
        ]);
        setHistory(res.data);
        setTotal(res.total);
        setCurrent(today);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : c.loadFailed);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [tab, debouncedSearch, showInactive, fuelFilter, unitFilter, page, pageSize]);

  useEffect(() => {
    fetchTab();
  }, [fetchTab]);

  const switchTab = (next: string) => {
    if (!next || next === tab) return;
    // `resetParams` on the tab param drops page / search / filters in the same write.
    setTabParam(next as MastersTab);
  };

  const paginator =
    !error && total > 0 ? (
      <Paginator
        currentPage={page}
        totalItems={total}
        itemsPerPage={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    ) : null;

  // ── Vehicle modal ────────────────────────────────────────────────────────────
  const [vehicleOpen, setVehicleOpen] = useState(false);
  const [vehicleDraft, setVehicleDraft] = useState<VehicleDraft>(blankVehicle);
  const [vehicleError, setVehicleError] = useState<string | null>(null);
  const [savingVehicle, setSavingVehicle] = useState(false);
  const [busyRowId, setBusyRowId] = useState<string | null>(null);

  const patchVehicle = (patch: Partial<VehicleDraft>) => {
    setVehicleError(null);
    setVehicleDraft((d) => {
      const next = { ...d, ...patch };
      // Energy type drives the allowed fuels: EV has exactly one, ICE three.
      if (patch.energyType && patch.energyType !== d.energyType) {
        const allowed = UJP_FUEL_TYPES_BY_ENERGY[patch.energyType];
        next.fuelType = allowed.includes(next.fuelType as UjpFuelType)
          ? next.fuelType
          : allowed.length === 1
            ? allowed[0]
            : '';
      }
      return next;
    });
  };

  const openVehicle = (row?: UjpVehicle) => {
    setVehicleDraft(row ? vehicleToDraft(row) : blankVehicle());
    setVehicleError(null);
    setVehicleOpen(true);
  };

  const upsertVehicleRow = (row: UjpVehicle) =>
    setVehicles((prev) => {
      const i = prev.findIndex((v) => v.id === row.id);
      if (i < 0) return [row, ...prev];
      const next = [...prev];
      next[i] = row;
      return next;
    });

  const saveVehicle = async () => {
    const invalid = validateVehicleDraft(vehicleDraft);
    if (invalid) {
      setVehicleError(invalid);
      return;
    }
    setSavingVehicle(true);
    try {
      const payload = toVehiclePayload(vehicleDraft);
      const saved = vehicleDraft.id
        ? await ujpAPI.updateVehicle(vehicleDraft.id, payload)
        : await ujpAPI.createVehicle(payload);
      upsertVehicleRow(saved);
      toast.success(
        vehicleDraft.id
          ? c.vehicles.updated(saved.plateNumber)
          : c.vehicles.created(saved.plateNumber)
      );
      setVehicleOpen(false);
      // A new / renamed unit type should show up in the filter options.
      setUnitTypesVersion((v) => v + 1);
    } catch (err) {
      setVehicleError(err instanceof Error ? err.message : c.vehicles.saveFailed);
    } finally {
      setSavingVehicle(false);
    }
  };

  const toggleVehicle = async (row: UjpVehicle) => {
    const draft = { ...vehicleToDraft(row), active: !row.active };
    // A row the DTO would reject (e.g. no fuel type) is fixed in the modal
    // instead of failing the toggle with a 400.
    if (validateVehicleDraft(draft)) {
      setVehicleDraft(draft);
      setVehicleError(validateVehicleDraft(draft));
      setVehicleOpen(true);
      return;
    }
    setBusyRowId(row.id);
    try {
      const saved = await ujpAPI.updateVehicle(row.id, toVehiclePayload(draft));
      upsertVehicleRow(saved);
      toast.success(
        saved.active
          ? c.vehicles.activated(saved.plateNumber)
          : c.vehicles.deactivated(saved.plateNumber)
      );
    } catch (err) {
      toast.error(c.vehicles.saveFailed, {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setBusyRowId(null);
    }
  };

  // ── Vendor modal ─────────────────────────────────────────────────────────────
  const [vendorOpen, setVendorOpen] = useState(false);
  const [vendorDraft, setVendorDraft] = useState<VendorDraft>(blankVendor);
  const [vendorError, setVendorError] = useState<string | null>(null);
  const [savingVendor, setSavingVendor] = useState(false);

  const patchVendor = (patch: Partial<VendorDraft>) => {
    setVendorError(null);
    setVendorDraft((d) => ({ ...d, ...patch }));
  };

  const openVendor = (row?: UjpSubconVendor) => {
    setVendorDraft(row ? vendorToDraft(row) : blankVendor());
    setVendorError(null);
    setVendorOpen(true);
  };

  const upsertVendorRow = (row: UjpSubconVendor) =>
    setVendors((prev) => {
      const i = prev.findIndex((v) => v.id === row.id);
      if (i < 0) return [row, ...prev];
      const next = [...prev];
      next[i] = row;
      return next;
    });

  const saveVendor = async () => {
    const invalid = validateVendorDraft(vendorDraft);
    if (invalid) {
      setVendorError(invalid);
      return;
    }
    setSavingVendor(true);
    try {
      const payload = toVendorPayload(vendorDraft);
      const saved = vendorDraft.id
        ? await ujpAPI.updateSubconVendor(vendorDraft.id, payload)
        : await ujpAPI.createSubconVendor(payload);
      upsertVendorRow(saved);
      toast.success(
        vendorDraft.id ? c.vendors.updated(saved.name) : c.vendors.created(saved.name)
      );
      setVendorOpen(false);
    } catch (err) {
      setVendorError(err instanceof Error ? err.message : c.vendors.saveFailed);
    } finally {
      setSavingVendor(false);
    }
  };

  const toggleVendor = async (row: UjpSubconVendor) => {
    setBusyRowId(row.id);
    try {
      const saved = await ujpAPI.updateSubconVendor(row.id, {
        ...toVendorPayload(vendorToDraft(row)),
        active: !row.active,
      });
      upsertVendorRow(saved);
      toast.success(
        saved.active ? c.vendors.activated(saved.name) : c.vendors.deactivated(saved.name)
      );
    } catch (err) {
      toast.error(c.vendors.saveFailed, {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setBusyRowId(null);
    }
  };

  // ── Price modal ──────────────────────────────────────────────────────────────
  const [priceOpen, setPriceOpen] = useState(false);
  const [priceDraft, setPriceDraft] = useState<PriceDraft>(blankPrice);
  const [priceError, setPriceError] = useState<string | null>(null);
  const [savingPrice, setSavingPrice] = useState(false);

  const patchPrice = (patch: Partial<PriceDraft>) => {
    setPriceError(null);
    setPriceDraft((d) => ({ ...d, ...patch }));
  };

  const openPrice = () => {
    setPriceDraft(blankPrice(fuelFilter || 'SOLAR'));
    setPriceError(null);
    setPriceOpen(true);
  };

  const savePrice = async () => {
    const invalid = validatePriceDraft(priceDraft);
    if (invalid) {
      setPriceError(invalid);
      return;
    }
    setSavingPrice(true);
    try {
      const saved = await ujpAPI.createEnergyPrice(toPricePayload(priceDraft));
      toast.success(
        c.energy.created(c.fuelType[saved.fuelType], saved.effectiveFrom)
      );
      setPriceOpen(false);
      fetchTab();
    } catch (err) {
      setPriceError(err instanceof Error ? err.message : c.energy.saveFailed);
    } finally {
      setSavingPrice(false);
    }
  };

  const currentByFuel = useMemo(
    () => new Map(current.map((p) => [p.fuelType, p])),
    [current]
  );

  // ── Columns ──────────────────────────────────────────────────────────────────
  const statusBadge = (active: boolean) => (
    <Badge status={active ? 'success' : 'neutral'} size="sm">
      {active ? c.active : c.inactive}
    </Badge>
  );

  const vehicleColumns: TableDataColumn<UjpVehicle>[] = [
    {
      key: 'plateNumber',
      title: c.vehicles.columns.plate,
      render: (_v, r) => (
        <span
          className={`font-mono text-[12px] font-semibold tabular-nums ${
            r.active ? 'text-text-strong-950' : 'text-text-soft-400'
          }`}
        >
          {r.plateNumber}
        </span>
      ),
    },
    {
      key: 'unitType',
      title: c.vehicles.columns.unitType,
      render: (_v, r) => (
        <span className="text-[13px] text-text-sub-600">{r.unitType || '—'}</span>
      ),
    },
    {
      key: 'energyType',
      title: c.vehicles.columns.energy,
      render: (_v, r) => (
        <Badge status={r.energyType === 'EV' ? 'information' : 'neutral'} size="sm">
          {c.energyType[r.energyType]}
        </Badge>
      ),
    },
    {
      key: 'fuelType',
      title: c.vehicles.columns.fuel,
      render: (_v, r) => (
        <span className="text-[13px] text-text-sub-600">
          {r.fuelType ? c.fuelType[r.fuelType] : '—'}
        </span>
      ),
    },
    {
      key: 'baseline',
      title: c.vehicles.columns.consumption,
      align: 'right',
      render: (_v, r) => (
        <span className="font-mono text-[12px] tabular-nums text-text-sub-600 whitespace-nowrap">
          {r.energyType === 'EV'
            ? r.konsumsiPerKm != null
              ? `${r.konsumsiPerKm} kWh/km`
              : '—'
            : r.baseline != null
              ? `${r.baseline} km/L`
              : '—'}
        </span>
      ),
    },
    {
      key: 'active',
      title: c.vehicles.columns.status,
      render: (_v, r) => statusBadge(r.active),
    },
    {
      key: 'actions',
      title: '',
      align: 'right',
      render: (_v, r) =>
        canEdit ? (
          <div className="inline-flex items-center justify-end gap-1">
            <Button
              tone="neutral"
              style="stroke"
              size="xs"
              aria-label={`${c.edit} ${r.plateNumber}`}
              onClick={() => openVehicle(r)}
            >
              {c.edit}
            </Button>
            <Button
              tone="neutral"
              style="ghost"
              size="xs"
              aria-label={`${r.active ? c.deactivate : c.activate} ${r.plateNumber}`}
              loading={busyRowId === r.id}
              disabled={busyRowId != null}
              onClick={() => toggleVehicle(r)}
            >
              {r.active ? c.deactivate : c.activate}
            </Button>
          </div>
        ) : null,
    },
  ];

  const priceColumns: TableDataColumn<UjpEnergyPrice>[] = [
    {
      key: 'fuelType',
      title: c.energy.columns.fuel,
      render: (_v, r) => (
        <span className="text-[13px] font-semibold text-text-strong-950">
          {c.fuelType[r.fuelType]}
        </span>
      ),
    },
    {
      key: 'region',
      title: 'Daerah',
      render: (_v, r) =>
        r.region ? (
          <span className="text-[13px] text-text-strong-950">{r.region}</span>
        ) : (
          <Badge status="neutral" size="sm">
            Global
          </Badge>
        ),
    },
    {
      key: 'price',
      title: c.energy.columns.price,
      align: 'right',
      render: (_v, r) => (
        <span className="font-mono text-[12px] tabular-nums text-text-strong-950 whitespace-nowrap">
          {formatRupiah(r.price)}
          <span className="text-text-soft-400">{unitFor(r.fuelType)}</span>
        </span>
      ),
    },
    {
      key: 'effectiveFrom',
      title: c.energy.columns.effectiveFrom,
      render: (_v, r) => (
        <span className="flex items-center gap-2">
          <span className="font-mono text-[12px] tabular-nums text-text-sub-600">
            {r.effectiveFrom}
          </span>
          {currentByFuel.get(r.fuelType)?.id === r.id && (
            <Badge status="success" size="sm">
              {c.energy.effectiveNow}
            </Badge>
          )}
        </span>
      ),
    },
  ];

  const vendorColumns: TableDataColumn<UjpSubconVendor>[] = [
    {
      key: 'name',
      title: c.vendors.columns.name,
      render: (_v, r) => (
        <span
          className={`text-[13px] font-semibold tracking-tight ${
            r.active ? 'text-text-strong-950' : 'text-text-soft-400'
          }`}
        >
          {r.name}
        </span>
      ),
    },
    {
      key: 'city',
      title: c.vendors.columns.city,
      render: (_v, r) => (
        <span className="text-[13px] text-text-sub-600">{r.city || '—'}</span>
      ),
    },
    {
      key: 'bankName',
      title: c.vendors.columns.bank,
      render: (_v, r) => (
        <span className="flex flex-col text-[12px] text-text-sub-600">
          <span>
            {r.bankName || '—'}
            {r.accountNumber ? (
              <span className="ml-2 font-mono tabular-nums">
                {canEdit ? r.accountNumber : maskAccount(r.accountNumber)}
              </span>
            ) : null}
          </span>
          {r.accountHolder && (
            <span className="text-[11px] text-text-soft-400">{r.accountHolder}</span>
          )}
        </span>
      ),
    },
    {
      key: 'picName',
      title: c.vendors.columns.pic,
      render: (_v, r) => (
        <span className="text-[12px] text-text-sub-600">
          {[r.picName, r.picPhone].filter(Boolean).join(' · ') || '—'}
        </span>
      ),
    },
    {
      key: 'active',
      title: c.vendors.columns.status,
      render: (_v, r) => statusBadge(r.active),
    },
    {
      key: 'actions',
      title: '',
      align: 'right',
      render: (_v, r) =>
        canEdit ? (
          <div className="inline-flex items-center justify-end gap-1">
            <Button
              tone="neutral"
              style="stroke"
              size="xs"
              aria-label={`${c.edit} ${r.name}`}
              onClick={() => openVendor(r)}
            >
              {c.edit}
            </Button>
            <Button
              tone="neutral"
              style="ghost"
              size="xs"
              aria-label={`${r.active ? c.deactivate : c.activate} ${r.name}`}
              loading={busyRowId === r.id}
              disabled={busyRowId != null}
              onClick={() => toggleVendor(r)}
            >
              {r.active ? c.deactivate : c.activate}
            </Button>
          </div>
        ) : null,
    },
  ];

  const emptyState = (title: string) =>
    error ? (
      <EmptyState size="sm">
        <EmptyStateTitle>{c.loadFailed}</EmptyStateTitle>
        <EmptyStateDescription>{error}</EmptyStateDescription>
        <EmptyStateActions>
          <Button tone="neutral" style="ghost" size="sm" onClick={fetchTab}>
            {c.retry}
          </Button>
        </EmptyStateActions>
      </EmptyState>
    ) : (
      <EmptyState size="sm">
        <EmptyStateTitle>{title}</EmptyStateTitle>
      </EmptyState>
    );

  const addButton = (() => {
    if (!canEdit) return null;
    if (tab === 'vehicles') {
      return (
        <Button onClick={() => openVehicle()}>
          <RiAddLine />
          {c.vehicles.add}
        </Button>
      );
    }
    if (tab === 'vendors') {
      return (
        <Button onClick={() => openVendor()}>
          <RiAddLine />
          {c.vendors.add}
        </Button>
      );
    }
    return (
      <Button onClick={openPrice}>
        <RiAddLine />
        {c.energy.add}
      </Button>
    );
  })();

  const allowedFuels = UJP_FUEL_TYPES_BY_ENERGY[vehicleDraft.energyType];

  return (
    <div className="space-y-5 p-6">
      <div className="flex flex-col gap-3 border-b border-stroke-soft-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="label-eyebrow mb-2">{c.eyebrow}</p>
          <h1 className="text-[28px] font-extrabold leading-none tracking-tight text-text-strong-950">
            {c.title}
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-text-sub-600">
            {c.subtitle}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button tone="neutral" style="stroke" onClick={fetchTab}>
            <RiRefreshLine />
            {UJP_COPY.page.refresh}
          </Button>
          <Button tone="neutral" style="ghost" asChild>
            <Link to="/ujp">
              <RiArrowLeftLine />
              {c.back}
            </Link>
          </Button>
          {addButton}
        </div>
      </div>

      {!canEdit && <Hint>{c.readOnly}</Hint>}

      <Card padding="none">
        <div className="flex flex-col gap-3 border-b border-stroke-soft-200 px-4 pt-1 pb-3 lg:flex-row lg:items-end lg:justify-between lg:pb-0">
          <Tabs value={tab} onValueChange={switchTab}>
            <TabsList variant="line" className="h-auto min-h-10 w-auto flex-wrap border-0">
              {TABS.map((k) => (
                <TabsTrigger key={k} value={k}>
                  {c.tabs[k]}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <div className="flex flex-wrap items-center gap-2 lg:pb-2">
            {tab !== 'energy' && (
              <SearchField
                key={tab}
                value={search}
                onChange={setSearch}
                placeholder={
                  tab === 'vehicles'
                    ? c.vehicles.searchPlaceholder
                    : c.vendors.searchPlaceholder
                }
                size="sm"
                className="min-w-[220px] max-w-xs flex-1"
              />
            )}
            <UjpMasterFilterPanel
              tab={tab}
              open={filterOpen}
              onOpenChange={handleFilterOpenChange}
              activeFilterCount={activeFilterCount}
              value={filterDraft}
              onChange={setFilterDraft}
              onApply={applyFilter}
              onReset={resetFilter}
              unitTypes={unitTypes}
              unitTypesLoading={unitTypesLoading}
            />
          </div>
        </div>

        {tab === 'energy' && (
          <div className="grid gap-3 border-b border-stroke-soft-200 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
            {UJP_FUEL_TYPES.map((f) => {
              const row = currentByFuel.get(f);
              return (
                <div
                  key={f}
                  className="rounded-gsm border border-stroke-soft-200 bg-bg-white-0 p-3"
                  data-testid={`current-price-${f}`}
                >
                  <p className={eyebrow}>{c.fuelType[f]}</p>
                  <p className="mt-2 font-mono text-[18px] font-bold leading-none tabular-nums text-text-strong-950">
                    {row ? (
                      <>
                        {formatRupiah(row.price)}
                        <span className="text-[11px] font-medium text-text-soft-400">
                          {unitFor(f)}
                        </span>
                      </>
                    ) : (
                      <span className="text-[13px] font-medium text-text-soft-400">
                        {c.energy.currentEmpty}
                      </span>
                    )}
                  </p>
                  <p className="mt-2 border-t border-stroke-soft-200 pt-2 font-mono text-[11px] tabular-nums text-text-sub-600">
                    {row ? `${c.energy.current} · ${row.effectiveFrom}` : c.energy.current}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        <div className="space-y-3 p-4">
          {tab === 'vehicles' && (
            <TableData<UjpVehicle>
              data={error ? [] : vehicles}
              columns={vehicleColumns}
              loading={loading}
              size="sm"
              emptyState={emptyState(c.vehicles.empty)}
            />
          )}
          {tab === 'energy' && (
            <TableData<UjpEnergyPrice>
              data={error ? [] : history}
              columns={priceColumns}
              loading={loading}
              size="sm"
              emptyState={emptyState(c.energy.empty)}
            />
          )}
          {tab === 'vendors' && (
            <TableData<UjpSubconVendor>
              data={error ? [] : vendors}
              columns={vendorColumns}
              loading={loading}
              size="sm"
              emptyState={emptyState(c.vendors.empty)}
            />
          )}
          {paginator}
        </div>
      </Card>

      {/* Vehicle create / edit */}
      <FormModal
        open={vehicleOpen}
        onClose={savingVehicle ? () => undefined : () => setVehicleOpen(false)}
        icon={<RiAddLine />}
        title={
          vehicleDraft.id
            ? c.vehicles.editTitle(vehicleDraft.plateNumber)
            : c.vehicles.createTitle
        }
        size="md"
        footer={
          <div className="flex items-center justify-between gap-3">
            <span className="min-h-4 text-[12px] text-error-base" role="alert">
              {vehicleError ?? ''}
            </span>
            <div className="flex items-center gap-2">
              <Button
                tone="neutral"
                style="stroke"
                onClick={() => setVehicleOpen(false)}
                disabled={savingVehicle}
              >
                {c.cancel}
              </Button>
              <Button onClick={saveVehicle} loading={savingVehicle}>
                {savingVehicle ? c.saving : c.save}
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field>
              <Label htmlFor="vehicle-plate" required>
                {c.vehicles.plate}
              </Label>
              <InputRoot size="md">
                <Input
                  id="vehicle-plate"
                  value={vehicleDraft.plateNumber}
                  placeholder={c.vehicles.platePlaceholder}
                  className="min-w-0 w-full font-mono uppercase"
                  maxLength={20}
                  disabled={savingVehicle}
                  onChange={(e) => patchVehicle({ plateNumber: e.target.value })}
                />
              </InputRoot>
            </Field>
            <Field>
              <Label htmlFor="vehicle-unit">{c.vehicles.unitType}</Label>
              <InputRoot size="md">
                <Input
                  className="min-w-0 w-full"
                  id="vehicle-unit"
                  value={vehicleDraft.unitType}
                  placeholder={c.vehicles.unitTypePlaceholder}
                  maxLength={100}
                  disabled={savingVehicle}
                  onChange={(e) => patchVehicle({ unitType: e.target.value })}
                />
              </InputRoot>
            </Field>
          </div>

          <Field>
            <Label>{c.vehicles.energy}</Label>
            <SegmentedControl
              value={vehicleDraft.energyType}
              onValueChange={(v: string) =>
                v && patchVehicle({ energyType: v as UjpEnergyType })
              }
              className="w-full [&>*]:flex-1"
            >
              {UJP_ENERGY_TYPES.map((e) => (
                <SegmentedItem key={e} value={e}>
                  {c.energyType[e]}
                </SegmentedItem>
              ))}
            </SegmentedControl>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field>
              <Label htmlFor="vehicle-fuel" required>
                {c.vehicles.fuel}
              </Label>
              <InputRoot size="md">
                <select
                  id="vehicle-fuel"
                  value={vehicleDraft.fuelType}
                  disabled={savingVehicle}
                  onChange={(e) =>
                    patchVehicle({ fuelType: e.target.value as UjpFuelType | '' })
                  }
                  className="h-full w-full cursor-pointer bg-transparent px-2 text-[13px] text-text-strong-950 outline-none"
                >
                  {allowedFuels.length > 1 && <option value="">—</option>}
                  {allowedFuels.map((f) => (
                    <option key={f} value={f}>
                      {c.fuelType[f]}
                    </option>
                  ))}
                </select>
              </InputRoot>
            </Field>
            {vehicleDraft.energyType === 'ICE' ? (
              <Field>
                <Label htmlFor="vehicle-baseline" required>
                  {c.vehicles.baseline}
                </Label>
                <InputRoot size="md">
                  <Input
                    id="vehicle-baseline"
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="0.1"
                    value={vehicleDraft.baseline}
                    className="min-w-0 w-full font-mono tabular-nums text-right"
                    disabled={savingVehicle}
                    onChange={(e) => patchVehicle({ baseline: e.target.value })}
                  />
                </InputRoot>
              </Field>
            ) : (
              <Field>
                <Label htmlFor="vehicle-konsumsi" required>
                  {c.vehicles.konsumsi}
                </Label>
                <InputRoot size="md">
                  <Input
                    id="vehicle-konsumsi"
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="0.01"
                    value={vehicleDraft.konsumsiPerKm}
                    className="min-w-0 w-full font-mono tabular-nums text-right"
                    disabled={savingVehicle}
                    onChange={(e) => patchVehicle({ konsumsiPerKm: e.target.value })}
                  />
                </InputRoot>
              </Field>
            )}
          </div>

          <Field>
            <Label htmlFor="vehicle-emoney">{c.vehicles.eMoney}</Label>
            <InputRoot size="md">
              <select
                id="vehicle-emoney"
                value={vehicleDraft.defaultEMoney}
                disabled={savingVehicle}
                onChange={(e) =>
                  patchVehicle({
                    defaultEMoney: e.target.value as UjpEMoney | '',
                  })
                }
                className="h-full w-full cursor-pointer bg-transparent px-2 text-[13px] text-text-strong-950 outline-none"
              >
                <option value="">{c.vehicles.eMoneyNone}</option>
                {(['NONE', 'FLAZZ', 'QRIS', 'FLAZZ_QRIS'] as UjpEMoney[]).map(
                  (k) => (
                    <option key={k} value={k}>
                      {UJP_COPY.cost.eMoneyOptions[k]}
                    </option>
                  )
                )}
              </select>
            </InputRoot>
          </Field>

          <Field>
            <Label htmlFor="vehicle-emoney-card">
              {c.vehicles.eMoneyCardNo}
            </Label>
            <InputRoot size="md">
              <Input
                id="vehicle-emoney-card"
                value={vehicleDraft.eMoneyCardNo}
                placeholder={c.vehicles.eMoneyCardNoPlaceholder}
                className="min-w-0 w-full font-mono"
                maxLength={100}
                disabled={savingVehicle}
                onChange={(e) => patchVehicle({ eMoneyCardNo: e.target.value })}
              />
            </InputRoot>
            <Hint>{c.vehicles.eMoneyCardNoHint}</Hint>
          </Field>

          <Hint>{c.vehicles.baselineHint}</Hint>

          {vehicleDraft.id && (
            <div className="flex items-center justify-between gap-3 rounded-gsm border border-stroke-soft-200 bg-bg-weak-50 p-3">
              <Label htmlFor="vehicle-active">{c.active}</Label>
              <Switch
                id="vehicle-active"
                aria-label={c.active}
                checked={vehicleDraft.active}
                disabled={savingVehicle}
                onCheckedChange={(active) => patchVehicle({ active })}
              />
            </div>
          )}
        </div>
      </FormModal>

      {/* Energy price — add a dated row */}
      <FormModal
        open={priceOpen}
        onClose={savingPrice ? () => undefined : () => setPriceOpen(false)}
        icon={<RiAddLine />}
        title={c.energy.createTitle}
        subtitle={c.energy.createSubtitle}
        size="sm"
        footer={
          <div className="flex items-center justify-between gap-3">
            <span className="min-h-4 text-[12px] text-error-base" role="alert">
              {priceError ?? ''}
            </span>
            <div className="flex items-center gap-2">
              <Button
                tone="neutral"
                style="stroke"
                onClick={() => setPriceOpen(false)}
                disabled={savingPrice}
              >
                {c.cancel}
              </Button>
              <Button onClick={savePrice} loading={savingPrice}>
                {savingPrice ? c.saving : c.save}
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          <Field>
            <Label htmlFor="price-fuel" required>
              {c.energy.fuel}
            </Label>
            <InputRoot size="md">
              <select
                id="price-fuel"
                value={priceDraft.fuelType}
                disabled={savingPrice}
                onChange={(e) => patchPrice({ fuelType: e.target.value as UjpFuelType })}
                className="h-full w-full cursor-pointer bg-transparent px-2 text-[13px] text-text-strong-950 outline-none"
              >
                {UJP_FUEL_TYPES.map((f) => (
                  <option key={f} value={f}>
                    {c.fuelType[f]}
                  </option>
                ))}
              </select>
            </InputRoot>
          </Field>
          <Field>
            <Label htmlFor="price-region">Daerah</Label>
            <InputRoot size="md">
              <Input
                className="min-w-0 w-full"
                id="price-region"
                value={priceDraft.region}
                placeholder="Kosongkan untuk harga global (semua daerah)"
                maxLength={120}
                disabled={savingPrice}
                onChange={(e) => patchPrice({ region: e.target.value })}
              />
            </InputRoot>
            <Hint>
              Harga khusus daerah ini menang atas harga global. Kosongkan =
              harga default semua daerah.
            </Hint>
          </Field>
          <Field>
            <Label htmlFor="price-amount" required>
              {c.energy.price}
            </Label>
            <MoneyInput
              id="price-amount"
              aria-label={c.energy.price}
              value={priceDraft.price}
              disabled={savingPrice}
              onChange={(v) => patchPrice({ price: v })}
            />
            {currentByFuel.get(priceDraft.fuelType) && (
              <Hint>
                {c.energy.current}: {formatRupiah(currentByFuel.get(priceDraft.fuelType)!.price)}
                {unitFor(priceDraft.fuelType)} ·{' '}
                {currentByFuel.get(priceDraft.fuelType)!.effectiveFrom}
              </Hint>
            )}
          </Field>
          <Field>
            <Label htmlFor="price-from" required>
              {c.energy.effectiveFrom}
            </Label>
            <InputRoot size="md">
              <Input
                className="min-w-0 w-full"
                id="price-from"
                type="date"
                value={priceDraft.effectiveFrom}
                disabled={savingPrice}
                onChange={(e) => patchPrice({ effectiveFrom: e.target.value })}
              />
            </InputRoot>
          </Field>
        </div>
      </FormModal>

      {/* Vendor create / edit */}
      <FormModal
        open={vendorOpen}
        onClose={savingVendor ? () => undefined : () => setVendorOpen(false)}
        icon={<RiAddLine />}
        title={vendorDraft.id ? c.vendors.editTitle(vendorDraft.name) : c.vendors.createTitle}
        size="md"
        footer={
          <div className="flex items-center justify-between gap-3">
            <span className="min-h-4 text-[12px] text-error-base" role="alert">
              {vendorError ?? ''}
            </span>
            <div className="flex items-center gap-2">
              <Button
                tone="neutral"
                style="stroke"
                onClick={() => setVendorOpen(false)}
                disabled={savingVendor}
              >
                {c.cancel}
              </Button>
              <Button onClick={saveVendor} loading={savingVendor}>
                {savingVendor ? c.saving : c.save}
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field className="col-span-2 sm:col-span-1">
              <Label htmlFor="vendor-name" required>
                {c.vendors.name}
              </Label>
              <InputRoot size="md">
                <Input
                  className="min-w-0 w-full"
                  id="vendor-name"
                  value={vendorDraft.name}
                  placeholder={c.vendors.namePlaceholder}
                  maxLength={200}
                  disabled={savingVendor}
                  onChange={(e) => patchVendor({ name: e.target.value })}
                />
              </InputRoot>
            </Field>
            <Field>
              <Label htmlFor="vendor-city">{c.vendors.city}</Label>
              <InputRoot size="md">
                <Input
                  className="min-w-0 w-full"
                  id="vendor-city"
                  value={vendorDraft.city}
                  maxLength={100}
                  disabled={savingVendor}
                  onChange={(e) => patchVendor({ city: e.target.value })}
                />
              </InputRoot>
            </Field>
          </div>

          <div className="space-y-3">
            <p className={eyebrow}>{c.vendors.columns.bank}</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Field>
                <Label htmlFor="vendor-bank">{c.vendors.bankName}</Label>
                <InputRoot size="md">
                  <Input
                    className="min-w-0 w-full"
                    id="vendor-bank"
                    value={vendorDraft.bankName}
                    maxLength={100}
                    disabled={savingVendor}
                    onChange={(e) => patchVendor({ bankName: e.target.value })}
                  />
                </InputRoot>
              </Field>
              <Field>
                <Label htmlFor="vendor-account">{c.vendors.accountNumber}</Label>
                <InputRoot size="md">
                  <Input
                    id="vendor-account"
                    value={vendorDraft.accountNumber}
                    inputMode="numeric"
                    className="min-w-0 w-full font-mono tabular-nums"
                    maxLength={50}
                    disabled={savingVendor}
                    onChange={(e) => patchVendor({ accountNumber: e.target.value })}
                  />
                </InputRoot>
              </Field>
              <Field className="col-span-2 sm:col-span-1">
                <Label htmlFor="vendor-holder">{c.vendors.accountHolder}</Label>
                <InputRoot size="md">
                  <Input
                    className="min-w-0 w-full"
                    id="vendor-holder"
                    value={vendorDraft.accountHolder}
                    maxLength={200}
                    disabled={savingVendor}
                    onChange={(e) => patchVendor({ accountHolder: e.target.value })}
                  />
                </InputRoot>
              </Field>
            </div>
          </div>

          <div className="space-y-3">
            <p className={eyebrow}>{c.vendors.columns.pic}</p>
            <div className="grid grid-cols-2 gap-3">
              <Field>
                <Label htmlFor="vendor-pic">{c.vendors.picName}</Label>
                <InputRoot size="md">
                  <Input
                    className="min-w-0 w-full"
                    id="vendor-pic"
                    value={vendorDraft.picName}
                    maxLength={200}
                    disabled={savingVendor}
                    onChange={(e) => patchVendor({ picName: e.target.value })}
                  />
                </InputRoot>
              </Field>
              <Field>
                <Label htmlFor="vendor-pic-phone">{c.vendors.picPhone}</Label>
                <InputRoot size="md">
                  <Input
                    id="vendor-pic-phone"
                    value={vendorDraft.picPhone}
                    inputMode="tel"
                    className="min-w-0 w-full font-mono tabular-nums"
                    maxLength={30}
                    disabled={savingVendor}
                    onChange={(e) => patchVendor({ picPhone: e.target.value })}
                  />
                </InputRoot>
              </Field>
            </div>
          </div>

          {vendorDraft.id && (
            <div className="flex items-center justify-between gap-3 rounded-gsm border border-stroke-soft-200 bg-bg-weak-50 p-3">
              <Label htmlFor="vendor-active">{c.active}</Label>
              <Switch
                id="vendor-active"
                aria-label={c.active}
                checked={vendorDraft.active}
                disabled={savingVendor}
                onCheckedChange={(active) => patchVendor({ active })}
              />
            </div>
          )}
        </div>
      </FormModal>
    </div>
  );
};

export default UjpMastersPage;
